<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4">
        <h1 class="sci-page-title-only">Abastecimentos Concluídos</h1>
        <p class="text-sm text-slate-500 font-medium mt-1">Consulta detalhada de todos os abastecimentos realizados no sistema</p>
      </div>

      <!-- ÁREA DE FILTRO -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
              <v-icon color="orange" small>mdi-filter-outline</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                  OPÇÕES DE FILTRAGEM
              </h2>
          </div>
          <div class="p-8">
              <v-row dense>
                <v-col cols="12" sm="6" md="3">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Inicial</label>
                  <Date
                    @dateValue="[(filter.startDate = $event)]"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Data Final</label>
                  <Date @dateValue="[(filter.endDate = $event)]" />
                </v-col>
                <v-col cols="12" sm="12" md="6">
                   <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Prefeitura</label>
                  <v-autocomplete
                    :disabled="userGroup == 1 ? false : true"
                    :items="prefectures"
                    v-model="filter.prefecture_id"
                    item-value="prefecture_id"
                    item-text="prefecture_social_name"
                    placeholder="Selecione a prefeitura"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                    @change="onPrefectureChange"
                    no-data-text="Nenhuma prefeitura encontrada"
                  />
                </v-col>
                <v-col cols="12" sm="12" md="4">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Posto / Fornecedor</label>
                  <v-autocomplete
                    :items="providers"
                    v-model="filter.provider_id"
                    item-value="provider_id"
                    item-text="provider_name"
                    placeholder="Todos os fornecedores"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                    no-data-text="Nenhum fornecedor encontrado"
                    clearable
                  />
                </v-col>
                <v-col cols="12" sm="12" md="4">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Veículo</label>
                  <v-autocomplete
                    :items="vehicles"
                    v-model="filter.vehicle_id"
                    item-value="vehicle_id"
                    item-text="vehicle_board"
                    placeholder="Todos os veículos"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                    clearable
                    no-data-text="Nenhum veículo encontrado"
                  >
                    <template slot="selection" slot-scope="{ item }">
                      <span class="text-xs">{{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}</span>
                    </template>
                    <template slot="item" slot-scope="{ item }">
                      <span class="text-xs">{{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}</span>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="12" md="4">
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-2">Motorista</label>
                  <v-autocomplete
                    :items="drivers"
                    item-value="driver_id"
                    item-text="driver_name"
                    v-model="filter.driver_id"
                    placeholder="Todos os motoristas"
                    outlined dense hide-details="auto"
                    class="bg-slate-50"
                    no-data-text="Nenhum motorista encontrado"
                    clearable
                  />
                </v-col>
              </v-row>

              <div class="flex justify-end gap-3 mt-8">
                  <button 
                    @click="search"
                    :disabled="!(filter.prefecture_id && filter.startDate && filter.endDate) || loader"
                    class="bg-orange-500 hover:bg-orange-600 text-white rounded-lg shadow-md shadow-orange-500/20 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-10 transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed text-[11px] font-bold uppercase tracking-widest"
                  >
                      <v-icon color="white" small>mdi-filter-variant</v-icon>
                      {{ loader ? 'FILTRANDO...' : 'FILTRAR RESULTADOS' }}
                  </button>
              </div>
          </div>
      </section>

      <!-- RESULTADO -->
      <section v-if="data.length > 0 || loader" class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <div class="flex items-center gap-3">
                  <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                  <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                      REGISTROS ENCONTRADOS
                  </h2>
              </div>
              <div class="flex gap-2">
                <button 
                  @click="exportToCSV"
                  :disabled="loader || loaderExport || data.length === 0"
                  class="bg-white/10 hover:bg-white/20 text-white font-bold text-[10px] px-5 py-2 rounded-lg border border-white/20 transition-all uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
                >
                  <v-icon small color="white">mdi-file-delimited-outline</v-icon>
                  EXPORTAR CSV
                </button>
                <v-btn
                  color="white"
                  outlined
                  depressed
                  class="rounded-lg border-white/20 bg-white/10 text-white font-bold text-[10px] tracking-widest h-9 px-5 uppercase"
                  :disabled="loader || loaderExport || data.length === 0"
                  :to="`/report/supplies/${filter.startDate}/${filter.endDate}/${filter.prefecture_id}/${filter.provider_id || 'all'}/${filter.vehicle_id || 'all'}/${filter.driver_id || 'all'}`"
                  target="_blank"
                >
                  <v-icon left small>mdi-file-pdf-box</v-icon>
                  GERAR PDF
                </v-btn>
              </div>
          </div>
          
          <v-data-table
            :loading="loader"
            :items="data"
            :headers="headers"
            :items-per-page="15"
            no-data-text="Nenhum abastecimento encontrado"
            loading-text="CARREGANDO ABASTECIMENTOS..."
            class="elevation-0"
          >
            <template v-slot:item.datehour="{ item }">
              <span class="text-xs font-semibold text-slate-600">{{ convertDateTime(item.datehour) }}</span>
            </template>
            <template v-slot:item.model_desc="{ item }">
              <div class="flex flex-col py-2">
                <span class="text-xs font-bold text-slate-800">{{ item.vehicle_board }}</span>
                <span class="text-[10px] text-slate-500 font-medium uppercase truncate max-w-[150px]">{{ item.brand_desc }} - {{ item.model_desc }}</span>
              </div>
            </template>
            <template v-slot:item.driver_name="{ item }">
                <span class="text-xs font-medium text-slate-700">{{ item.driver_name }}</span>
            </template>
            <template v-slot:item.authorizer="{ item }">
              <span v-if="item.is_autorized" class="text-[10px] font-black px-2 py-1 rounded bg-emerald-50 text-emerald-600 uppercase tracking-wider">{{ item.authorizer }}</span>
              <span v-else class="text-[10px] font-bold px-2 py-1 rounded bg-slate-50 text-slate-400 uppercase tracking-wider italic">Sem Autorização</span>
            </template>
            <template v-slot:item.supply_liters_stocked="{ item }">
              <span class="text-xs font-bold text-slate-800 font-mono">{{ realMaskII((Number(item.supply_liters_stocked) || 0).toFixed(3)) }} L</span>
            </template>
            <template v-slot:item.last_km="{ item }">
              <span class="text-xs text-slate-500 font-mono">{{ item.last_km.toLocaleString('pt-BR') }}</span>
            </template>
            <template v-slot:item.supply_km="{ item }">
              <span class="text-xs font-bold text-slate-800 font-mono">{{ item.supply_km.toLocaleString('pt-BR') }}</span>
            </template>
            <template v-slot:item.average="{ item }">
              <span class="text-[10px] font-black px-2 py-1 rounded bg-blue-50 text-blue-600 uppercase tracking-tight">
                {{
                  Number(item.supply_liters_stocked) > 0 
                  ? `${((Number(item.supply_km) - Number(item.last_km)) / Number(item.supply_liters_stocked)).toFixed(2).toLocaleString("pt-BR")} KM/L`
                  : '0 KM/L'
                }}
              </span>
            </template>
            <template v-slot:item.action="{ item }">
              <div class="flex items-center gap-1 justify-end">
                <v-tooltip bottom color="emerald">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn 
                      icon 
                      small 
                      v-bind="attrs" 
                      v-on="on" 
                      class="text-emerald-500 hover:bg-emerald-50 transition-colors"
                      @click="conferir(item)"
                      :disabled="item.supply_confered"
                    >
                      <v-icon small>{{ item.supply_confered ? 'mdi-calendar-check' : 'mdi-calendar-check-outline' }}</v-icon>
                    </v-btn>
                  </template>
                  <span class="text-[10px] font-bold uppercase tracking-widest">{{ item.supply_confered ? 'Já Conferido' : 'Marcar como Conferido' }}</span>
                </v-tooltip>

                <v-tooltip bottom color="orange">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn 
                      icon 
                      small 
                      v-bind="attrs" 
                      v-on="on" 
                      class="text-orange-500 hover:bg-orange-50 transition-colors"
                      @click="abrirHistorico(item)"
                    >
                      <v-icon small>mdi-history</v-icon>
                    </v-btn>
                  </template>
                  <span class="text-[10px] font-bold uppercase tracking-widest">Ver Detalhes e Fotos</span>
                </v-tooltip>
              </div>
            </template>
            </v-data-table>

            <!-- Totais Section -->
            <div class="px-6 py-4 border-t border-slate-100 flex justify-end items-center gap-8 bg-slate-50/30">
                <div class="flex items-center gap-3">
                    <span class="font-bold text-[11px] text-slate-400 uppercase tracking-widest">TOTAL DE REGISTROS</span>
                    <span class="text-xs font-black text-slate-700 bg-slate-100 border border-slate-200 rounded px-3 py-1">
                      {{ data.length }}
                    </span>
                </div>
                <div class="flex items-center gap-3">
                    <span class="font-bold text-[11px] text-slate-400 uppercase tracking-widest">TOTAL LITROS</span>
                    <span class="text-xs font-black text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-1">
                      {{ totalLiters }} L
                    </span>
                </div>
            </div>
      </section>

      <!-- MODAL EDITAR -->
      <v-dialog v-model="showModal" max-width="800px" persistent scrollable>
        <div class="bg-white rounded-xl overflow-hidden shadow-2xl font-sans flex flex-col max-h-[90vh]">
          <!-- Header -->
          <div class="px-6 py-4 flex justify-between items-center shrink-0" style="background-color: #333333;">
            <h3 class="text-white font-bold uppercase tracking-widest text-sm flex items-center gap-2">
              <v-icon color="orange" small>mdi-pencil</v-icon>
              {{ modalTitle }}
            </h3>
            <button @click="[showModal = false, clearForm()]" class="text-slate-400 hover:text-white transition-colors rounded-full p-1 hover:bg-slate-700">
              <v-icon small color="inherit">mdi-close</v-icon>
            </button>
          </div>

          <!-- Body -->
          <div class="p-6 overflow-y-auto">
            <v-form ref="validForm" v-if="!loadingModal" v-model="validForm" lazy-validation>
                <v-row dense>
                  <v-col cols="12">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Veículo</label>
                    <v-autocomplete
                      :rules="fieldRules"
                      :items="vehicles_form"
                      item-value="vehicle_id"
                      item-text="vehicle_board"
                      v-model="dataForm.vehicle_id"
                      placeholder="Selecione o veículo"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                      @change="loadItems(dataForm.provider_id, dataForm.vehicle_id, dataForm.prefecture_id)"
                    >
                      <template slot="selection" slot-scope="{ item }">
                        <span class="text-sm font-medium">{{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}</span>
                      </template>
                      <template slot="item" slot-scope="{ item }">
                        <span class="text-sm">{{ item.brand_desc }} - {{ item.model_desc }} - {{ item.vehicle_board }}</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Fornecedor</label>
                    <v-autocomplete
                      :rules="fieldRules"
                      :items="providers_form"
                      @change="loadItems(dataForm.provider_id, dataForm.vehicle_id, dataForm.prefecture_id)"
                      item-value="provider_id"
                      item-text="provider_name"
                      v-model="dataForm.provider_id"
                      placeholder="Selecione o fornecedor"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Item/Produto</label>
                    <v-autocomplete
                      :rules="fieldRules"
                      :items="items_form"
                      item-value="item_id"
                      item-text="item_description_desc"
                      v-model="dataForm.item_id"
                      placeholder="Selecione o item"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                    >
                      <template v-slot:selection="{ item }">
                        <span class="text-sm font-medium">{{ item.item_description_desc }} <span class="text-emerald-600 ml-2">R$ {{ realMask((Number(item.item_price) || 0).toFixed(2)) }}</span></span>
                      </template>
                      <template v-slot:item="{ item }">
                        <span class="text-sm">{{ item.item_description_desc }} <span class="text-emerald-600 ml-2">R$ {{ realMask((Number(item.item_price) || 0).toFixed(2)) }}</span></span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Motorista</label>
                    <v-autocomplete
                      :items="drivers_form"
                      item-value="driver_id"
                      item-text="driver_name"
                      v-model="dataForm.driver_id"
                      :rules="fieldRules"
                      placeholder="Selecione o motorista"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Data e Hora</label>
                    <v-text-field
                      v-mask="'00/00/0000 00:00'"
                      v-model="dataForm.datehour"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                      :rules="fieldRules"
                    />
                  </v-col>
                  
                  <v-divider class="my-6 col-span-full"></v-divider>

                  <v-col cols="12" md="4">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Preço do Dia</label>
                    <v-text-field
                      v-model="dataForm.supply_price_day"
                      v-mask-decimal.br="3"
                      outlined dense hide-details="auto"
                      class="bg-slate-50 font-bold text-emerald-600"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Preço Licitado</label>
                    <v-text-field
                      v-model="dataForm.supply_bidding_price"
                      v-mask-decimal.br="3"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Lts Abastecidos</label>
                    <v-text-field
                      v-model="dataForm.supply_liters_stocked"
                      v-mask-decimal.br="3"
                      outlined dense hide-details="auto"
                      class="bg-slate-50 font-bold"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Número da NF-e</label>
                    <v-text-field
                      v-model="dataForm.supply_coupon"
                      outlined dense hide-details="auto"
                      class="bg-slate-50"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mt-4 mb-1">Km Atual</label>
                    <v-text-field
                      v-model="dataForm.supply_km"
                      outlined dense hide-details="auto"
                      class="bg-slate-50 font-mono"
                    />
                  </v-col>
                </v-row>
            </v-form>
            <div v-else class="flex flex-col items-center justify-center py-12">
              <v-progress-circular indeterminate color="orange" size="48"></v-progress-circular>
              <span class="mt-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Carregando dados...</span>
            </div>
          </div>

          <!-- Footer -->
          <div class="bg-slate-100 px-6 py-4 flex justify-end gap-3 border-t border-slate-200 shrink-0">
            <button 
              @click="[showModal = false, clearForm()]" 
              class="px-6 py-2 rounded-lg text-slate-500 font-bold uppercase tracking-widest text-[11px] hover:bg-slate-200 transition-colors"
            >
              CANCELAR
            </button>
            <button 
              @click="updateSupply(dataForm)" 
              :disabled="!validForm || loadingSubmit"
              class="px-8 h-10 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold uppercase tracking-widest text-[11px] shadow-md shadow-orange-500/20 transition-all transform hover:-translate-y-0.5 flex items-center gap-2"
            >
              <v-icon v-if="loadingSubmit" small class="mdi-spin" color="white">mdi-loading</v-icon>
              <v-icon v-else small color="white">mdi-check</v-icon>
              SALVAR ALTERAÇÕES
            </button>
          </div>
        </div>
      </v-dialog>

      <!-- MODAL HISTÓRICO DETALHADO -->
      <v-dialog v-model="showHistoryModal" max-width="900px" scrollable transition="dialog-bottom-transition">
        <div class="bg-slate-50 rounded-xl overflow-hidden shadow-2xl font-sans flex flex-col max-h-[90vh]">
          <!-- Header -->
          <div class="px-6 py-4 flex justify-between items-center shrink-0 shadow-sm z-10" style="background-color: #333333;">
            <div class="flex flex-col">
              <h3 class="text-white font-bold uppercase tracking-widest text-sm flex items-center gap-2">
                <v-icon color="orange" small>mdi-history</v-icon>
                DETALHES DO ABASTECIMENTO
              </h3>
              <span class="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">ID: {{ selectedHistoryItem.supply_id || '---' }}</span>
            </div>
            <button @click="showHistoryModal = false" class="text-slate-400 hover:text-white transition-colors rounded-full p-1 hover:bg-slate-700">
              <v-icon small color="inherit">mdi-close</v-icon>
            </button>
          </div>

          <!-- Body -->
          <div class="p-6 overflow-y-auto bg-slate-50/50">
            <div class="space-y-6">
              
              <!-- SEÇÃO: INFORMAÇÕES BÁSICAS -->
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <label class="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">VEÍCULO</label>
                  <div class="text-xs font-bold text-slate-800 leading-tight">
                    {{ selectedHistoryItem.brand_desc }} {{ selectedHistoryItem.model_desc }}
                  </div>
                  <div class="text-[10px] font-medium text-slate-500 mt-1 uppercase tracking-wider">
                    PLACA: {{ selectedHistoryItem.vehicle_board }}
                  </div>
                </div>
                
                <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <label class="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">MOTORISTA</label>
                  <div class="text-xs font-bold text-slate-800 leading-tight">
                    {{ selectedHistoryItem.driver_name || 'NÃO INFORMADO' }}
                  </div>
                  <div class="text-[10px] font-medium text-slate-500 mt-1 uppercase tracking-wider">
                    DATA: {{ convertDateTime(selectedHistoryItem.datehour) }}
                  </div>
                </div>

                <div class="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                  <label class="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">FORNECEDOR</label>
                  <div class="text-xs font-bold text-slate-800 leading-tight">
                    {{ selectedHistoryItem.provider_name }}
                  </div>
                  <div class="text-[10px] font-bold text-emerald-600 mt-1 uppercase tracking-wider">
                    ITEM: {{ selectedHistoryItem.item_description_desc }}
                  </div>
                </div>
              </div>

              <!-- SEÇÃO: VALORES E LITROS -->
              <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div class="px-4 py-2 bg-slate-100 border-b border-slate-200 flex items-center gap-2">
                  <v-icon small color="slate-500">mdi-cash-register</v-icon>
                  <span class="text-[10px] font-black text-slate-500 uppercase tracking-widest">PRODUÇÃO E FATURAMENTO</span>
                </div>
                <div class="p-4 grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <label class="block text-[9px] font-bold text-slate-400 uppercase mb-1">Status</label>
                    <v-chip x-small :color="selectedHistoryItem.supply_status === 'CONCLUIDO' ? 'success' : 'orange'" dark label class="font-bold text-[9px]">
                      {{ selectedHistoryItem.supply_status }}
                    </v-chip>
                  </div>
                  <div>
                    <label class="block text-[9px] font-bold text-slate-400 uppercase mb-1">NF-e</label>
                    <div class="text-sm font-black text-slate-700 tracking-widest">{{ selectedHistoryItem.supply_coupon || '---' }}</div>
                  </div>
                  <div class="bg-orange-50 -m-2 p-2 rounded-lg border border-orange-100 px-4">
                    <label class="block text-[9px] font-bold text-orange-400 uppercase mb-1">Litros Abastecidos</label>
                    <div class="text-sm font-black text-orange-600">{{ realMaskII((Number(selectedHistoryItem.supply_liters_stocked) || 0).toFixed(3)) }} L</div>
                  </div>
                  <div class="bg-emerald-50 -m-2 p-2 rounded-lg border border-emerald-100 px-4">
                    <label class="block text-[9px] font-bold text-emerald-400 uppercase mb-1">Valor Total</label>
                    <div class="text-sm font-black text-emerald-600">
                      R$ {{ realMask((Number(selectedHistoryItem.supply_value) || 0).toFixed(2)) }}
                    </div>
                  </div>
                </div>
                <div class="px-4 py-3 bg-slate-50 border-t border-slate-200 flex justify-between">
                  <div class="flex gap-6">
                    <div>
                      <v-tooltip bottom color="slate-800">
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Preço Licitado: </span>
                        </template>
                        <span class="text-[10px]">Preço acordado em licitação</span>
                      </v-tooltip>
                      <span class="text-[11px] font-bold text-slate-600">R$ {{ realMaskII((Number(selectedHistoryItem.supply_bidding_price) || 0).toFixed(3)) }}</span>
                    </div>
                  </div>
                  <div v-if="selectedHistoryItem.authorizer">
                    <span class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">Liberado por: </span>
                    <span class="text-[11px] font-black text-slate-800 uppercase">{{ selectedHistoryItem.authorizer }}</span>
                  </div>
                </div>
              </div>

              <!-- SEÇÃO: QUILOMETRAGEM -->
              <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div class="px-4 py-2 bg-slate-100 border-b border-slate-200 flex justify-between items-center">
                  <div class="flex items-center gap-2">
                    <v-icon small color="slate-500">mdi-speedometer</v-icon>
                    <span class="text-[10px] font-black text-slate-500 uppercase tracking-widest">DESEMPENHO E QUILOMETRAGEM</span>
                  </div>
                  <div class="bg-slate-800 px-3 py-1 rounded-full shadow-lg border border-slate-600">
                     <span class="text-[10px] font-black text-white uppercase tracking-widest">MÉDIA: </span>
                     <span class="text-xs font-black text-orange-400 ml-1">
                        {{
                          Number(selectedHistoryItem.supply_liters_stocked) > 0 
                          ? `${((Number(selectedHistoryItem.supply_km) - Number(selectedHistoryItem.last_km)) / Number(selectedHistoryItem.supply_liters_stocked)).toFixed(2).toLocaleString("pt-BR")} KM/L`
                          : '0 KM/L'
                        }}
                     </span>
                  </div>
                </div>
                <div class="p-4 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div class="flex flex-col items-center justify-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <label class="text-[10px] font-bold text-slate-400 uppercase mb-1">Km Anterior</label>
                    <div class="text-lg font-black text-slate-600">{{ Number(selectedHistoryItem.last_km || 0).toLocaleString('pt-BR') }}</div>
                  </div>
                  <div class="flex flex-col items-center justify-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <label class="text-[10px] font-bold text-slate-400 uppercase mb-1">Km Atual</label>
                    <div class="text-lg font-black text-slate-800">{{ Number(selectedHistoryItem.supply_km || 0).toLocaleString('pt-BR') }}</div>
                  </div>
                  <div class="flex flex-col items-center justify-center p-3 bg-orange-50 rounded-lg border border-orange-100">
                    <label class="text-[10px] font-bold text-orange-400 uppercase mb-1">Km Percorrido</label>
                    <div class="text-lg font-black text-orange-600">{{ (Number(selectedHistoryItem.supply_km) - Number(selectedHistoryItem.last_km)) || 0 }}</div>
                  </div>
                </div>
              </div>

              <!-- SEÇÃO: FOTOS -->
              <div class="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div class="px-4 py-2 bg-slate-100 border-b border-slate-200 flex items-center gap-2">
                  <v-icon small color="slate-500">mdi-camera</v-icon>
                  <span class="text-[10px] font-black text-slate-500 uppercase tracking-widest">REGISTROS FOTOGRÁFICOS</span>
                </div>
                <div class="p-4">
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <!-- Foto Hodômetro -->
                    <div class="space-y-2">
                      <label class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center">HODÔMETRO</label>
                      <div class="aspect-video bg-slate-100 rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" @click="abrirFoto(selectedHistoryItem.foto_km)">
                        <img v-if="selectedHistoryItem.foto_km" :src="getUrlFoto(selectedHistoryItem.foto_km)" class="w-full h-full object-cover transition-transform group-hover:scale-110" />
                        <div v-else class="w-full h-full flex items-center justify-center text-slate-300">
                          <v-icon large>mdi-camera-off</v-icon>
                        </div>
                        <div v-if="selectedHistoryItem.foto_km" class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <v-icon color="white">mdi-magnify-plus</v-icon>
                        </div>
                      </div>
                    </div>

                    <!-- Foto Bomba -->
                     <div class="space-y-2">
                      <label class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center">BOMBA</label>
                      <div class="aspect-video bg-slate-100 rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" @click="abrirFoto(selectedHistoryItem.foto_bomba)">
                        <img v-if="selectedHistoryItem.foto_bomba" :src="getUrlFoto(selectedHistoryItem.foto_bomba)" class="w-full h-full object-cover transition-transform group-hover:scale-110" />
                        <div v-else class="w-full h-full flex items-center justify-center text-slate-300">
                          <v-icon large>mdi-camera-off</v-icon>
                        </div>
                        <div v-if="selectedHistoryItem.foto_bomba" class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <v-icon color="white">mdi-magnify-plus</v-icon>
                        </div>
                      </div>
                    </div>

                    <!-- Foto Cupom -->
                    <div class="space-y-2">
                      <label class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center">NF-e</label>
                      <div class="aspect-video bg-slate-100 rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" @click="abrirFoto(selectedHistoryItem.foto_cupom)">
                        <img v-if="selectedHistoryItem.foto_cupom" :src="getUrlFoto(selectedHistoryItem.foto_cupom)" class="w-full h-full object-cover transition-transform group-hover:scale-110" />
                        <div v-else class="w-full h-full flex items-center justify-center text-slate-300">
                          <v-icon large>mdi-camera-off</v-icon>
                        </div>
                        <div v-if="selectedHistoryItem.foto_cupom" class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <v-icon color="white">mdi-magnify-plus</v-icon>
                        </div>
                      </div>
                    </div>

                    <!-- Foto Motorista -->
                    <div class="space-y-2">
                      <label class="block text-[9px] font-bold text-slate-400 uppercase tracking-wider text-center">MOTORISTA</label>
                      <div class="aspect-video bg-slate-100 rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" @click="abrirFoto(selectedHistoryItem.foto_motorista || selectedHistoryItem.foto_confirmacao)">
                        <img v-if="selectedHistoryItem.foto_motorista || selectedHistoryItem.foto_confirmacao" :src="getUrlFoto(selectedHistoryItem.foto_motorista || selectedHistoryItem.foto_confirmacao)" class="w-full h-full object-cover transition-transform group-hover:scale-110" />
                        <div v-else class="w-full h-full flex items-center justify-center text-slate-300">
                          <v-icon large>mdi-camera-off</v-icon>
                        </div>
                        <div v-if="selectedHistoryItem.foto_motorista || selectedHistoryItem.foto_confirmacao" class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <v-icon color="white">mdi-magnify-plus</v-icon>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- JUSTIFICATIVA (SE HOUVER) -->
              <div v-if="selectedHistoryItem.justificativa" class="bg-amber-50 rounded-xl border border-amber-200 p-4 shadow-sm">
                <div class="flex items-center gap-2 mb-2">
                  <v-icon small color="amber-600">mdi-message-alert-outline</v-icon>
                  <span class="text-[10px] font-black text-amber-600 uppercase tracking-widest">OBSERVAÇÕES / JUSTIFICATIVA</span>
                </div>
                <p class="text-xs text-amber-800 font-medium leading-relaxed">
                  {{ selectedHistoryItem.justificativa }}
                </p>
              </div>

            </div>
          </div>

          <!-- Footer -->
          <div class="bg-white px-6 py-4 flex justify-end gap-3 border-t border-slate-200 shrink-0">
            <button 
              @click="showHistoryModal = false" 
              class="px-8 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white font-bold uppercase tracking-widest text-[11px] shadow-lg transition-all transform hover:-translate-y-0.5 active:translate-y-0"
            >
              FECHAR DETALHES
            </button>
          </div>
        </div>
      </v-dialog>

      <v-snackbar
        multi-line
        :color="snack.color"
        v-model="snack.show"
        transition="fade-transition"
        top right
      >
        <span class="text-[11px] font-bold uppercase tracking-widest">{{ snack.message }}</span>
        <template v-slot:action="{ attrs }">
          <v-btn dark text v-bind="attrs" @click="snack.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
import Date from "@/components/InputDate.vue";
export default {
  components: { Date },
  mixins: [Helpers],
  data() {
    return {
      userGroup: null,
      loader: true,
      loaderExport: false,
      data: [],
      providers: [],
      vehicles: [],
      drivers: [],
      prefectures: [],
      showHistoryModal: false,
      selectedHistoryItem: {},
      snack: {
        show: false,
        message: "",
        color: "",
      },
      filter: {
        endDate: "",
        startDate: "",
        provider_id: null,
        prefecture_id: null,
        vehicle_id: null,
        driver_id: null,
        exclude_adjusted: true,
      },
      headers: [
        { text: "Motorista", value: "driver_name" },
        { text: "Veículo", value: "model_desc" },
        { text: "Liberação", value: "authorizer", align: "center" },
        { text: "Data e Hora", value: "datehour", align: "center" },
        { text: "NF-e", value: "supply_coupon", align: "center" },
        { text: "Km Ant.", value: "last_km", align: "center" },
        { text: "Km Atual", value: "supply_km", align: "center" },
        { text: "Litros", value: "supply_liters_stocked", align: "center" },
        { text: "Média", value: "average", align: "center" },
        { text: "Ações", align: "right", value: "action", sortable: false },
      ],
      // NOVOS ESTADOS
      showModal: false,
      modalTitle: "Editar Abastecimento",
      loadingSubmit: false,
      loadingModal: false,
      validForm: false,
      providers_form: [],
      drivers_form: [],
      items_form: [],
      vehicles_form: [],
      fieldRules: [(v) => !!v || "Campo obrigatório"],
      dataForm: {
        supply_id: null,
        prefecture_id: null,
        vehicle_id: null,
        provider_id: null,
        driver_id: null,
        item_id: null,
        supply_price_day: "",
        supply_bidding_price: "",
        supply_liters_stocked: "",
        supply_coupon: "",
        supply_km: "",
        datehour: "",
        supply_status: "",
      },
    };
  },
  created() {
    this.initialize();
  },
  computed: {
    totalLiters() {
      if (!this.data || !this.data.length) return '0,000';
      const total = this.data.reduce((acc, item) => acc + (Number(item.supply_liters_stocked) || 0), 0);
      return this.realMaskII(total.toFixed(3));
    }
  },
  methods: {
    async initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);

      this.filter.prefecture_id = this.$store.getters.getUserData.table_id;
      this.userGroup = this.$store.getters.getUserData.group_id;
      
      if (this.filter.prefecture_id) {
        this.getVehicles(this.filter.prefecture_id);
        this.getProviders(this.filter.prefecture_id);
        this.getDrivers(this.filter.prefecture_id);
      }

      try {
        const res = await this.$http.get("/prefectures");
        this.prefectures = res.data;
      } catch (error) {
        console.error(error);
      } finally {
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      }
    },
    onPrefectureChange() {
      if (this.filter.prefecture_id) {
        this.getVehicles(this.filter.prefecture_id);
        this.getProviders(this.filter.prefecture_id);
        this.getDrivers(this.filter.prefecture_id);
      }
    },
    async search() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);

      try {
        const res = await this.$http.put("/supplies/billings", this.filter);
        this.data = res.data;
      } catch (error) {
        this.snack.show = true;
        this.snack.message = "Erro ao buscar abastecimentos!";
        this.snack.color = "error";
      } finally {
        this.loader = false;
        this.$store.dispatch("setLoader", this.loader);
      }
    },
    conferir(item) {
      let c = confirm("Deseja marcar este abastecimento como conferido?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .put(`/supplies/single/${item.supply_id}`, { update_simples: true, supply_confered: true })
          .then(() => {
            this.snack.show = true;
            this.snack.message = "Abastecimento conferido com sucesso!";
            this.snack.color = "success";
            this.search();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    abrirFoto(foto) {
      const url = this.getUrlFoto(foto);
      if (!url) {
        this.snack.message = "Foto não disponível!";
        this.snack.color = "warning";
        this.snack.show = true;
        return;
      }
      window.open(url, "_blank");
    },
    abrirHistorico(item) {
      this.selectedHistoryItem = item;
      this.showHistoryModal = true;
    },
    getUrlFoto(foto) {
      if (!foto) return null;
      const baseURL = "https://api.sigfrotagov.com.br/uploads/";
      return baseURL + foto?.replace('https://api.sigfrotagov.com.br/uploads/', '');
    },
    exportToCSV() {
      if (!this.data.length) return;

      const exportableHeaders = this.headers.filter(h => h.value && h.value !== 'action');
      const headerLabels = exportableHeaders.map(h => h.text);
      const headerKeys = exportableHeaders.map(h => h.value);

      const rows = this.data.map(item => {
        const cols = headerKeys.map(key => {
          let value = item[key];

          if (key === 'datehour' && value) {
            value = this.convertDateTime(value);
          } else if (key === 'supply_liters_stocked' && value != null) {
            value = this.realMaskII((Number(value) || 0).toFixed(3));
          } else if (key === 'supply_km' && value != null) {
            value = (Number(item.supply_km) || 0).toLocaleString('pt-BR');
          } else if (key === 'model_desc') {
            value = `${item.brand_desc} - ${item.model_desc} - ${item.vehicle_board}`;
          } else if (key === 'last_km' && value != null) {
            value = (Number(item.last_km) || 0).toLocaleString('pt-BR');
          } else if (key === 'average') {
            value = Number(item.supply_liters_stocked) > 0 
              ? `${((Number(item.supply_km) - Number(item.last_km)) / Number(item.supply_liters_stocked)).toFixed(2).toLocaleString("pt-BR")} KM/L`
              : '0 KM/L';
          }

          return JSON.stringify(value ?? "");
        });

        return cols.join(';');
      });

      const csvContent = [headerLabels.join(';'), ...rows].join('\r\n');
      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;"
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", `relatorio_abastecimentos.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async editar(item) {
      this.showModal = true;
      this.loadingModal = true;

      this.dataForm = {
        supply_id: item.supply_id,
        prefecture_id: item.prefecture_id,
        vehicle_id: item.vehicle_id,
        provider_id: item.provider_id,
        driver_id: item.driver_id,
        item_id: item.item_id,
        datehour: this.convertDateTime(item.datehour),
        supply_price_day: this.convertDecimalNumber((Number(item.supply_price_day) || 0).toFixed(3)),
        supply_bidding_price: this.convertDecimalNumber((Number(item.supply_bidding_price) || 0).toFixed(3)),
        supply_liters_stocked: this.convertDecimalNumber((Number(item.supply_liters_stocked) || 0).toFixed(3)),
        supply_coupon: item.supply_coupon ? item.supply_coupon : "",
        supply_km: item.supply_km ? item.supply_km : "",
        supply_status: item.supply_status,
      };

      await this.getVehicles(item.prefecture_id, true);
      await this.getDrivers(item.prefecture_id, true);
      await this.getProviders(item.prefecture_id, true);
      await this.loadItems(item.provider_id, item.vehicle_id, item.prefecture_id);

      this.loadingModal = false;
    },
    async concluir(item) {
      let c = confirm("Deseja concluir este abastecimento?");
      if (c) {
        try {
          this.loader = true;
          this.$store.dispatch("setLoader", true);
          await this.$http.put(`/supplies/single/${item.supply_id}`, { 
            update_simples: true, 
            supply_status: "CONCLUIDO" 
          });
          this.snack.show = true;
          this.snack.message = "Abastecimento concluído com sucesso!";
          this.snack.color = "success";
          this.search();
        } catch (error) {
          this.snack.show = true;
          this.snack.message = "Erro ao concluir abastecimento!";
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        }
      }
    },
    async cancelar(item) {
      let c = confirm("Deseja cancelar a autorização deste abastecimento?");
      if (c) {
        try {
          this.loader = true;
          this.$store.dispatch("setLoader", true);
          await this.$http.put(`/supplies/single/${item.supply_id}`, { 
            update_simples: true, 
            supply_status: "CANCELADO" 
          });
          this.snack.show = true;
          this.snack.message = "Autorização cancelada com sucesso!";
          this.snack.color = "success";
          this.search();
        } catch (error) {
          this.snack.show = true;
          this.snack.message = "Erro ao cancelar autorização!";
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        }
      }
    },
    async updateSupply(data) {
      if (this.$refs.validForm.validate()) {
        try {
          this.loadingSubmit = true;

          const payload = { ...data };
          delete payload.supply_id;
          delete payload.supply_status;
          delete payload.prefecture_id;

          payload.datehour = this.formatarStringData2(payload.datehour);
          payload.supply_price_day = this.convertStringNumber(payload.supply_price_day);
          payload.supply_bidding_price = this.convertStringNumber(payload.supply_bidding_price);
          payload.supply_liters_stocked = this.convertStringNumber(payload.supply_liters_stocked);

          payload.supply_value = this.round2(payload.supply_price_day * payload.supply_liters_stocked);
          payload.supply_bidding_value = this.round2(payload.supply_bidding_price * payload.supply_liters_stocked);
          
          await this.$http.put(`/supplies/single/${data.supply_id}`, { update_simples: true, ...payload })
          
          this.snack.show = true;
          this.snack.message = "Abastecimento atualizado com sucesso!";
          this.snack.color = "success";
          this.loadingSubmit = false;
          this.showModal = false;
          this.clearForm();
          this.search();
        } catch (error) {
          this.snack.show = true;
          this.snack.color = "error";
          this.snack.message = error.message;
          this.loadingSubmit = false;
        }
      }
    },
    async loadItems(provider_id, vehicle_id, prefecture_id) {
      if (provider_id && vehicle_id && prefecture_id) {
        const res = await this.$http.get(`/supplies/providers/${provider_id}/${vehicle_id}/${prefecture_id}`);
        this.items_form = res.data;
      } else {
        this.items_form = [];
      }
    },
    clearForm() {
      this.dataForm = {
        supply_id: null,
        prefecture_id: null,
        vehicle_id: null,
        provider_id: null,
        driver_id: null,
        item_id: null,
        supply_price_day: "",
        supply_bidding_price: "",
        supply_liters_stocked: "",
        supply_coupon: "",
        supply_km: "",
        datehour: "",
        supply_status: "",
      };
      this.providers_form = [];
      this.items_form = [];
      this.drivers_form = [];
      this.vehicles_form = [];
      this.validForm = false;
    },
    // Ajustar getVehicles, getDrivers e getProviders para suportar carregamento do formulário
    async getVehicles(prefecture_id, is_form = false) {
      if (!prefecture_id) return;
      const res = await this.$http.get(`/vehicles/supplies/${prefecture_id}`);
      if (is_form) {
        this.vehicles_form = res.data;
      } else {
        this.vehicles = res.data;
      }
    },
    async getProviders(prefecture_id, is_form = false) {
      if (!prefecture_id) return;
      const res = await this.$http.get(`/providers/prefecture/${prefecture_id}`);
      if (is_form) {
        this.providers_form = res.data;
      } else {
        this.providers = res.data;
      }
    },
    async getDrivers(prefecture_id, is_form = false) {
      if (!prefecture_id) return;
      try {
        const res = await this.$http.get(`/drivers/prefectures/${prefecture_id}`);
        if (is_form) {
          this.drivers_form = res.data;
        } else {
          this.drivers = res.data;
        }
      } catch (error) {
        console.error("Erro ao carregar motoristas:", error);
      }
    },
  },
};
</script>

<style scoped>
</style>